import { ChevronLeft, ChevronRight, Minus, Plus, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import type { StorefrontProduct } from '@/features/storefront/api/storefront';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { cn } from '@/lib/cn';

/**
 * The full-screen look at one product image.
 *
 * A photo on the page is sized to fit a column, which is the wrong size for
 * deciding whether a weave, a stitch or a finish is what a shopper wants. This
 * lifts the widest rendition out of the layout and hands it over: buttons for
 * people who look for buttons, the wheel and a pinch for people who never do,
 * and the keyboard for people who never touch a pointer.
 *
 * Everything is one scale plus one offset from the centre, and the offset is
 * clamped against the picture's own edges, so no gesture can push the photo
 * off the screen and leave a shopper looking at an empty rectangle.
 */

const MIN_SCALE = 1;
const MAX_SCALE = 4;
/** One press of a button, or one detent of the wheel. */
const STEP = 0.5;

function clampScale(value: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
}

interface Offset {
  x: number;
  y: number;
}

const CENTRED: Offset = { x: 0, y: 0 };

export interface ProductZoomProps {
  images: StorefrontProduct['images'];
  /** Which image the shopper opened, and what the arrows move away from. */
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** The product's name, for an image the merchant left without alt text. */
  fallbackAlt: string;
}

export function ProductZoom({
  images,
  index,
  onIndexChange,
  onClose,
  fallbackAlt,
}: ProductZoomProps) {
  const copy = useShopContent();
  const [scale, setScale] = useState(MIN_SCALE);
  const [offset, setOffset] = useState<Offset>(CENTRED);
  const stage = useRef<HTMLDivElement>(null);
  const picture = useRef<HTMLImageElement>(null);
  /** Live pointers on the stage: one pans, two pinch. */
  const pointers = useRef(new Map<number, Offset>());
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);
  /** Whether the gesture in progress travelled far enough to be a drag. */
  const dragged = useRef(false);

  const image = images[index];
  const many = images.length > 1;

  /**
   * How far the picture may be pushed before its own edge comes inside the
   * stage. Measured rather than assumed: the photo is drawn `contain`, so its
   * rendered size depends on both its aspect ratio and the window's.
   */
  const clampOffset = useCallback(
    (next: Offset, atScale: number): Offset => {
      const box = stage.current?.getBoundingClientRect();
      const shown = picture.current;
      if (!box || !shown) return next;
      // A bounding rect reports the *transformed* box, so the untransformed
      // size is what it measures divided by the scale it was drawn at.
      const drawn = shown.getBoundingClientRect();
      const maxX = Math.max(0, ((drawn.width / scale) * atScale - box.width) / 2);
      const maxY = Math.max(0, ((drawn.height / scale) * atScale - box.height) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, next.x)),
        y: Math.min(maxY, Math.max(-maxY, next.y)),
      };
    },
    [scale],
  );

  /**
   * Scale to `target`, keeping whatever sits under `anchor` - a pointer, or
   * the middle of the stage when a button asked - under it afterwards.
   */
  const zoomTo = useCallback(
    (target: number, anchor?: Offset) => {
      const next = clampScale(target);
      if (next === scale) return;
      if (next === MIN_SCALE) {
        setScale(next);
        setOffset(CENTRED);
        return;
      }
      const box = stage.current?.getBoundingClientRect();
      const point =
        box && anchor
          ? { x: anchor.x - (box.left + box.width / 2), y: anchor.y - (box.top + box.height / 2) }
          : CENTRED;
      setScale(next);
      setOffset((previous) =>
        clampOffset(
          {
            x: point.x - ((point.x - previous.x) * next) / scale,
            y: point.y - ((point.y - previous.y) * next) / scale,
          },
          next,
        ),
      );
    },
    [clampOffset, scale],
  );

  const reset = useCallback(() => {
    setScale(MIN_SCALE);
    setOffset(CENTRED);
  }, []);

  /** A different photo starts fresh - a crop from the last one means nothing here. */
  const show = useCallback(
    (next: number) => {
      reset();
      onIndexChange((next + images.length) % images.length);
    },
    [images.length, onIndexChange, reset],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const handlers: Record<string, () => void> = {
        Escape: onClose,
        '+': () => zoomTo(scale + STEP),
        '=': () => zoomTo(scale + STEP),
        '-': () => zoomTo(scale - STEP),
        _: () => zoomTo(scale - STEP),
        '0': reset,
        ArrowLeft: () => (many ? show(index - 1) : undefined),
        ArrowRight: () => (many ? show(index + 1) : undefined),
      };
      const handler = handlers[event.key];
      if (!handler) return;
      event.preventDefault();
      handler();
    }
    document.addEventListener('keydown', onKeyDown);

    // The page behind must not scroll under the pointer while a gesture that
    // looks like scrolling is being used to drive the picture.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [index, many, onClose, reset, scale, show, zoomTo]);

  /**
   * The wheel gets a listener of our own because React's `onWheel` is passive,
   * and a passive handler cannot stop the browser from scrolling the page as
   * well as zooming the picture.
   */
  useEffect(() => {
    const element = stage.current;
    if (!element) return;

    function onWheel(event: WheelEvent) {
      event.preventDefault();
      zoomTo(scale + (event.deltaY < 0 ? STEP : -STEP), { x: event.clientX, y: event.clientY });
    }

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [scale, zoomTo]);

  if (!image) return null;

  /** The gap between the two fingers of a pinch. */
  function pointerSpread(): number {
    const [first, second] = [...pointers.current.values()];
    if (!first || !second) return 0;
    return Math.hypot(first.x - second.x, first.y - second.y);
  }

  /**
   * A press on one of the buttons standing over the picture is that button's,
   * not the start of a pan.
   *
   * Capturing the pointer for a pan retargets every later pointer event - and
   * the `click` the browser synthesises from them - to the stage, so without
   * this the arrows over the photo could be pressed and nothing would happen.
   */
  function onControl(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest('button') !== null;
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (onControl(event.target)) return;
    dragged.current = false;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    // Capture so a drag that leaves the stage still pans, where the browser
    // has it - jsdom, and a synthetic event, do not.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (pointers.current.size === 2) {
      pinchStart.current = { distance: pointerSpread(), scale };
    }
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    if (Math.hypot(event.clientX - previous.x, event.clientY - previous.y) > 4) {
      dragged.current = true;
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size >= 2) {
      const start = pinchStart.current;
      const spread = pointerSpread();
      const [first, second] = [...pointers.current.values()];
      if (start && start.distance > 0 && spread > 0 && first && second) {
        zoomTo((start.scale * spread) / start.distance, {
          x: (first.x + second.x) / 2,
          y: (first.y + second.y) / 2,
        });
      }
      return;
    }

    if (scale === MIN_SCALE) return;
    const dx = event.clientX - previous.x;
    const dy = event.clientY - previous.y;
    setOffset((current) => clampOffset({ x: current.x + dx, y: current.y + dy }, scale));
  }

  function onPointerUp(event: PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  }

  const zoomed = scale > MIN_SCALE;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={copy('product_page.zoomed', { product: fallbackAlt })}
      // `h-dvh` rather than `inset-0` alone: on a phone the browser's own
      // toolbars sit inside the layout viewport, and a bar pinned to the
      // bottom of that viewport - the zoom controls - lands underneath them.
      // The dynamic viewport is the part actually being looked at.
      //
      // `touch-none` on the whole overlay, not just the stage: a pinch that
      // lands on either bar would otherwise be the *browser's* zoom, and the
      // browser zooms the visual viewport while a fixed overlay stays laid out
      // against the layout viewport - which walks the controls at the edges
      // off the side of the screen. The picture has a zoom of its own; the
      // page underneath must not get a second one.
      className="fixed inset-0 z-50 flex h-dvh touch-none flex-col overflow-hidden overscroll-none bg-black/90"
    >
      <div
        className="flex items-center justify-between gap-3 p-3"
        // A phone's notch and its rounded corners eat the edges of the screen;
        // the close button must not be one of the things they eat.
        // A full rem of inset rather than the usual three-quarters: this is the
        // only way out of the viewer, so it is the one control that must stay
        // clear of a rounded corner, a notch, or a viewport sitting a few
        // pixels off where the layout thinks it is.
        style={{
          paddingTop: 'max(1rem, env(safe-area-inset-top))',
          paddingRight: 'max(1rem, env(safe-area-inset-right))',
          paddingLeft: 'max(1rem, env(safe-area-inset-left))',
        }}
      >
        {many ? (
          <span className="min-w-0 ps-1 text-sm text-white/70 tabular-nums">
            {index + 1} / {images.length}
          </span>
        ) : (
          <span />
        )}
        {/* The glyph alone. A label beside it reads better on a desktop, but
            it is the first thing a narrow screen crops, and a half-cut word
            is worse than no word - the circled cross is understood anyway. */}
        <button
          type="button"
          aria-label={copy('common.close')}
          onClick={onClose}
          className="grid size-10 shrink-0 place-items-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>

      <div
        ref={stage}
        // `touch-none` hands every touch to the handlers below; without it the
        // browser claims the drag for its own pan-and-zoom and the picture
        // never moves.
        className={cn(
          'relative flex min-h-0 flex-1 touch-none items-center justify-center overflow-hidden select-none',
          zoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={(event) =>
          zoomed ? reset() : zoomTo(2, { x: event.clientX, y: event.clientY })
        }
        // The dark margin around the photo dismisses, the way every other
        // lightbox does - but only a tap that landed there, at rest, and did
        // not travel, so neither a pan nor a double-tap on the photo closes
        // the thing the shopper is still looking at.
        onClick={(event) => {
          if (event.target === event.currentTarget && !zoomed && !dragged.current) onClose();
        }}
      >
        {/* Always the widest rung: a picture the shopper is about to zoom
            into is the one place a phone-sized copy would show its pixels. */}
        <StorefrontImage
          ref={picture}
          url={image.url}
          sizes="1600px"
          priority
          fade={false}
          placeholder={false}
          alt={image.alt_text ?? fallbackAlt}
          draggable={false}
          className="max-h-full max-w-full object-contain"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
        />

        {many ? (
          <>
            <ZoomArrow
              side="left"
              label={copy('product_page.previous_image')}
              onClick={() => show(index - 1)}
            />
            <ZoomArrow
              side="right"
              label={copy('product_page.next_image')}
              onClick={() => show(index + 1)}
            />
          </>
        ) : null}
      </div>

      <div
        className="flex items-center justify-center gap-2 p-4"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <ZoomButton label={copy('product_page.zoom_out')} onClick={() => zoomTo(scale - STEP)} disabled={!zoomed}>
          <Minus className="size-5" aria-hidden />
        </ZoomButton>
        <span aria-live="polite" className="w-16 text-center text-sm text-white tabular-nums">
          {Math.round(scale * 100)}%
        </span>
        <ZoomButton
          label={copy('product_page.zoom_in')}
          onClick={() => zoomTo(scale + STEP)}
          disabled={scale >= MAX_SCALE}
        >
          <Plus className="size-5" aria-hidden />
        </ZoomButton>
        <ZoomButton label={copy('product_page.zoom_reset')} onClick={reset} disabled={!zoomed}>
          <RotateCcw className="size-4" aria-hidden />
        </ZoomButton>
      </div>
    </div>,
    document.body,
  );
}

function ZoomButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="grid size-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function ZoomArrow({
  side,
  label,
  onClick,
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
}) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20',
        side === 'left' ? 'start-3' : 'end-3',
      )}
    >
      <Icon className="size-6 rtl-flip" aria-hidden />
    </button>
  );
}
